<template>
  <div class="frozen-bills">
    <div class="frozen-bills__bar">
      <strong>{{ $t('frozenShipment.bills_title', {stop: stopLabel}) }}</strong>
      <span v-if="loading" class="frozen-bills__note">{{ $t('frozenShipment.loading') }}</span>
      <span v-else-if="generatedAt" class="frozen-bills__note">
        {{ $t('frozenShipment.bills_total', {total: fmt(total)}) }} · {{ $t('frozenShipment.as_of', {time: time(generatedAt)}) }}
      </span>
    </div>
    <p v-if="truncated" class="frozen-bills__note">{{ $t('frozenShipment.bills_truncated', {max: fmt(maxRows), total: fmt(stopCount)}) }}</p>

    <div v-if="error" class="notification is-warning is-light">
      {{ $t('frozenShipment.unavailable_' + error) }}
      <button class="button is-small" style="margin-left: 8px" @click.stop="fetch()">{{ $t('frozenShipment.retry') }}</button>
    </div>
    <div v-else class="table-container">
      <table class="table is-fullwidth is-narrow frozen-bills__table">
        <thead>
          <tr>
            <th>{{ $t('frozenShipment.col_bill') }}</th>
            <th class="has-text-right">{{ $t('frozenShipment.col_idle') }}</th>
            <th>{{ $t('frozenShipment.col_last_scan') }}</th>
            <th>{{ $t('frozenShipment.col_from') }}</th>
            <th>{{ $t('frozenShipment.col_to') }}</th>
            <th>{{ $t('frozenShipment.col_status') }}</th>
            <th>{{ $t('frozenShipment.col_state') }}</th>
            <th>{{ $t('frozenShipment.col_created') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="bill in rows" :key="bill.shipment_id">
            <td class="cell-nowrap">
              <a @click.stop="$router.push({name: 'list.shipment.detail', params: {id: bill.shipment_id}})">{{ bill.shipment_number }}</a>
              <span v-if="bill.parcels_here > 1" class="tag is-light frozen-bills__parcels">{{ $t('frozenShipment.parcels_here', {count: bill.parcels_here}) }}</span>
            </td>
            <td class="has-text-right cell-nowrap"><strong>{{ $t('frozenShipment.idle_hours', {hours: fmt(bill.idle_hours)}) }}</strong></td>
            <td class="cell-nowrap">
              {{ time(bill.last_scan_at) }}
              <small v-if="bill.mode === 'in_transit' && bill.left_from"><br>{{ $t('frozenShipment.left_from', {place: bill.left_from}) }}</small>
            </td>
            <td>{{ bill.from_branch }}<small v-if="bill.from_province"><br>{{ bill.from_province }}</small></td>
            <td>{{ bill.to_branch }}<small v-if="bill.to_province"><br>{{ bill.to_province }}</small></td>
            <td>{{ statusName(bill.status) }}</td>
            <td>
              {{ stateName(bill) }}
              <small v-if="bill.mode === 'other'"><br>{{ eventName(bill.event) }}</small>
            </td>
            <td class="cell-nowrap">{{ time(bill.created_at) }}</td>
          </tr>
          <tr v-if="!rows.length && !loading">
            <td colspan="8" class="has-text-centered">{{ $t('frozenShipment.bills_empty') }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="pages > 1" class="frozen-bills__pager">
      <button class="button is-small" :disabled="page <= 1 || loading" @click.stop="go(page - 1)">{{ $t('frozenShipment.prev') }}</button>
      <span>{{ $t('frozenShipment.page_of', {page, pages}) }}</span>
      <button class="button is-small" :disabled="page >= pages || loading" @click.stop="go(page + 1)">{{ $t('frozenShipment.next') }}</button>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import {fetchFrozenBills, reasonOf} from '@/services/frozenShipmentService';

const PER_PAGE = 50;

export default {
  name: 'FrozenBills',
  props: {
    // {mode, stop_kind, stop_id, total, ...} from the stop table
    stop: {type: Object, required: true},
    // {scope, days, status, state, bucket} from the page
    filters: {type: Object, required: true},
    stopLabel: {type: String, required: true},
  },
  data() {
    return {
      page: 1,
      rows: [],
      total: 0,
      truncated: false,
      maxRows: 0,
      generatedAt: null,
      loading: false,
      error: null,
      requestSeq: 0,
    };
  },
  computed: {
    pages() {
      return Math.max(1, Math.ceil(this.total / PER_PAGE));
    },
    // The count the summary holds for this stop, used when the list is cut.
    stopCount() {
      return this.stop[this.filters.bucket || 'total'];
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    async fetch() {
      const seq = ++this.requestSeq;
      this.loading = true;
      this.error = null;
      try {
        const params = {
          scope: this.filters.scope,
          days: this.filters.days,
          mode: this.stop.mode,
          stop_kind: this.stop.stop_kind,
          page: this.page,
          per_page: PER_PAGE,
        };
        if (this.stop.stop_id !== null) params.stop_id = this.stop.stop_id;
        if (this.filters.bucket) params.bucket = this.filters.bucket;
        if (this.filters.status !== '') params.status = this.filters.status;
        if (this.filters.state !== '') params.state = this.filters.state;

        const result = await fetchFrozenBills(params);
        if (seq !== this.requestSeq) return;
        // The list can shrink between pages. Without this an empty last page would also hide the pager.
        if (!result.rows.length && result.total > 0 && this.page > 1) {
          this.page = Math.ceil(result.total / PER_PAGE);
          await this.fetch();
          return;
        }
        this.rows = result.rows;
        this.total = result.total;
        this.truncated = result.truncated;
        this.maxRows = result.max_rows;
        this.generatedAt = result.generated_at;
      } catch (e) {
        if (seq !== this.requestSeq) return;
        this.rows = [];
        this.total = 0;
        this.truncated = false;
        this.generatedAt = null;
        this.error = reasonOf(e);
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    go(page) {
      this.page = page;
      this.fetch();
    },
    statusName(name) {
      return this.$te('common.' + name) ? this.$t('common.' + name) : name;
    },
    stateName(bill) {
      if (bill.state_id === null) return '-';
      return this.$te('frozenShipment.state_' + bill.state_id) ? this.$t('frozenShipment.state_' + bill.state_id) : bill.state;
    },
    eventName(event) {
      return this.$te('frozenShipment.event_' + event) ? this.$t('frozenShipment.event_' + event) : event;
    },
    fmt(value) {
      return new Intl.NumberFormat('en-US').format(value || 0);
    },
    time(value) {
      return value ? moment(value).format('DD/MM/YYYY HH:mm') : '-';
    },
  },
};
</script>

<style lang="scss" scoped>
.frozen-bills {
  text-align: left;
  cursor: default;
}

.frozen-bills__bar {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 0.88rem;
}

.frozen-bills__note {
  margin-bottom: 6px;
  font-size: 0.78rem;
  color: #52514e;
}

.frozen-bills__table {
  font-size: 0.82rem;
  background: transparent;

  th,
  td {
    text-align: left;
  }

  th,
  .cell-nowrap {
    white-space: nowrap;
  }

  th.has-text-right,
  td.has-text-right {
    font-variant-numeric: tabular-nums;
    text-align: right;
  }

  small {
    color: #52514e;
  }
}

.frozen-bills__parcels {
  height: 1.5em;
  margin-left: 6px;
  font-size: 0.68rem;
}

.frozen-bills__pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 8px;
  font-size: 0.82rem;
}
</style>
